import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {message} from "antd";
import {AxiosError} from "axios";
import {AnalyticsEvent} from "../../../utils/analytics";
import {Transaction} from "../domain/transaction.entity";
import {
  PaginatedResult,
  TransactionFilterParams,
  TransactionExportParams,
} from "../domain/transaction.types";
import {createTransactionRepository} from "../data/repositories";

// ==================== Legacy API Response Types ====================
// These types match the old API response structure for backwards compatibility

export type LegacyLocalTransactionResponse = {
  id: number;
  full_name: string;
  service_name: string | null;
  transaction_id?: number;
  source_table?: string;
  offering_type: string;
  email: string;
  currency: string;
  amount: number;
  reference: string;
  is_discounted?: boolean;
  charges?: number;
  charge_bearer?: "customer" | "coach";
  discount_code?: string | null;
  payment_date: string;
  status?: "Active" | "Completed" | "Paid" | "Pending Conversion";
  status_name?: "Active" | "Completed" | "Paid" | "Pending Conversion";
  discount?: number;
};

export type LegacyForeignTransactionResponse = {
  offering_type: string;
  customer_name: string;
  service_name: string;
  customer_email: string;
  price: string;
  currency: string;
  exchange_rate: string;
  converted_price: string;
  payment_date: string;
  status?: number;
  status_name?: "Active" | "Completed" | "Paid" | "Pending Conversion";
  reference: string;
};

export type LegacyPaginationMeta = {
  next_page: number;
  per_page: number;
  current_page: number;
  previous_page: number;
  total_pages: number;
};

type LegacyLocalTransactionsResponse = {
  data: {localTransactions: LegacyLocalTransactionResponse[]};
  meta: LegacyPaginationMeta;
};

type LegacyForeignTransactionsResponse = {
  data: {fxTransactions: LegacyForeignTransactionResponse[]};
  meta: LegacyPaginationMeta;
};

// ==================== Converters ====================

/**
 * Converts Transaction entity to legacy local API response format.
 * Enables backwards compatibility with existing components.
 */
export const toLegacyLocalTransaction = (
  t: Transaction
): LegacyLocalTransactionResponse => ({
  id: t.id,
  full_name: t.customerName,
  service_name: t.serviceName,
  transaction_id: t.transactionId,
  source_table: t.sourceTable,
  offering_type: t.offeringType,
  email: t.customerEmail,
  currency: t.currency,
  amount: t.amount,
  reference: t.reference,
  is_discounted: t.isDiscounted,
  charges: t.charges,
  charge_bearer: t.chargeBearer,
  discount_code: t.discountCode,
  payment_date: t.paymentDate,
  // Map domain status back to API status values
  status: t.status === "COMPLETED" ? "Paid" : "Active",
  status_name: t.status === "COMPLETED" ? "Paid" : "Active",
  discount: t.discount,
});

/**
 * Converts Transaction entity to legacy foreign API response format.
 */
export const toLegacyForeignTransaction = (
  t: Transaction
): LegacyForeignTransactionResponse => ({
  offering_type: t.offeringType,
  customer_name: t.customerName,
  service_name: t.serviceName || "",
  customer_email: t.customerEmail,
  price: t.amount.toString(),
  currency: t.currency,
  exchange_rate: t.exchangeRate || "",
  converted_price: t.convertedPrice || "",
  payment_date: t.paymentDate,
  status_name: t.status === "COMPLETED" ? "Completed" : "Pending Conversion",
  reference: t.reference,
});

/**
 * Converts domain pagination to legacy API format.
 */
const toLegacyMeta = (
  meta: PaginatedResult<Transaction>["meta"]
): LegacyPaginationMeta => ({
  current_page: meta.currentPage,
  total_pages: meta.totalPages,
  per_page: meta.perPage,
  next_page: meta.nextPage ?? 0,
  previous_page: meta.previousPage ?? 0,
});

// Query keys for cache management
export const TRANSACTION_QUERY_KEYS = {
  all: ["transactions"] as const,
  local: (page: number, limit: number) =>
    [...TRANSACTION_QUERY_KEYS.all, "local", page, limit] as const,
  foreign: (page: number, limit: number) =>
    [...TRANSACTION_QUERY_KEYS.all, "foreign", page, limit] as const,
};

/**
 * Hook for fetching paginated local transactions.
 */
export const useGetLocalTransactions = (
  enabled: boolean = true,
  page: number = 1,
  limit: number = 10
) => {
  const repository = createTransactionRepository();

  return useQuery<PaginatedResult<Transaction>, AxiosError<{message: string}>>({
    queryKey: TRANSACTION_QUERY_KEYS.local(page, limit),
    queryFn: () => repository.getLocalTransactions({page, limit}),
    enabled,
    refetchOnWindowFocus: true,
  });
};

/**
 * Hook for fetching paginated foreign currency transactions.
 */
export const useGetForeignTransactions = (
  enabled: boolean = true,
  page: number = 1,
  limit: number = 10
) => {
  const repository = createTransactionRepository();

  return useQuery<PaginatedResult<Transaction>, AxiosError<{message: string}>>({
    queryKey: TRANSACTION_QUERY_KEYS.foreign(page, limit),
    queryFn: () => repository.getForeignTransactions({page, limit}),
    enabled,
    refetchOnWindowFocus: true,
  });
};

/**
 * Hook for filtering local transactions.
 * Returns a mutation that can be triggered with filter params.
 */
export const useFilterLocalTransactions = () => {
  const repository = createTransactionRepository();

  return useMutation<
    PaginatedResult<Transaction>,
    AxiosError<{message: string}>,
    TransactionFilterParams
  >({
    mutationFn: (params) => repository.filterLocalTransactions(params),
    onError: (error) => {
      message.error(error.message);
      AnalyticsEvent("Coach transactions", "Filter transactions failed");
    },
    onSuccess: () => {
      AnalyticsEvent("Coach transactions", "Filter transactions success");
    },
  });
};

/**
 * Hook for filtering foreign currency transactions.
 * Returns a mutation that can be triggered with filter params.
 * Returns domain Transaction entities.
 */
export const useDomainFilterForeignTransactions = () => {
  const repository = createTransactionRepository();

  return useMutation<
    PaginatedResult<Transaction>,
    AxiosError<{message: string}>,
    TransactionFilterParams
  >({
    mutationFn: (params) => repository.filterForeignTransactions(params),
    onError: (error) => {
      message.error(error.message);
      AnalyticsEvent("Coach foreign transactions", "Filter foreign transactions failed");
    },
    onSuccess: () => {
      AnalyticsEvent("Coach foreign transactions", "Filter foreign transactions success");
    },
  });
};

/**
 * Hook for exporting transactions to CSV.
 */
export const useExportTransactionsCSV = () => {
  const repository = createTransactionRepository();

  return useMutation<unknown, AxiosError<{message: string}>, TransactionExportParams>({
    mutationFn: (params) => repository.exportTransactionsCSV(params),
    onError: (error) => {
      message.error(error.message);
      AnalyticsEvent("Coach transactions", "Coach transactions export CSV fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Coach transactions", "Coach transactions export CSV success");
    },
  });
};

/**
 * Hook for invalidating transaction queries.
 * Useful after mutations that affect transaction data.
 */
export const useInvalidateTransactions = () => {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () =>
      queryClient.invalidateQueries({queryKey: TRANSACTION_QUERY_KEYS.all}),
    invalidateLocal: () =>
      queryClient.invalidateQueries({
        queryKey: [...TRANSACTION_QUERY_KEYS.all, "local"],
      }),
    invalidateForeign: () =>
      queryClient.invalidateQueries({
        queryKey: [...TRANSACTION_QUERY_KEYS.all, "foreign"],
      }),
  };
};

// ==================== Legacy-Compatible Hooks ====================
// These hooks maintain backwards compatibility with existing components
// by returning the old API response shape.

/**
 * Legacy-compatible hook for fetching local transactions.
 * Returns data in the old API response format.
 *
 * @deprecated Use useGetLocalTransactions for new code.
 */
export const useGetCoachTransactions = (
  enabled: boolean = true,
  page: number = 1,
  limit: number = 10
) => {
  const repository = createTransactionRepository();

  return useQuery<LegacyLocalTransactionsResponse, AxiosError<{message: string}>>({
    queryKey: ["coach_transactions", page, limit],
    queryFn: async () => {
      const result = await repository.getLocalTransactions({page, limit});
      return {
        data: {localTransactions: result.data.map(toLegacyLocalTransaction)},
        meta: toLegacyMeta(result.meta),
      };
    },
    enabled,
    refetchOnWindowFocus: true,
  });
};

/**
 * Legacy-compatible hook for fetching foreign transactions.
 * Returns data in the old API response format.
 *
 * @deprecated Use useGetForeignTransactions for new code.
 */
export const useGetCoachForeignTransactions = (
  enabled: boolean = true,
  page: number = 1,
  limit: number = 10
) => {
  const repository = createTransactionRepository();

  return useQuery<LegacyForeignTransactionsResponse, AxiosError<{message: string}>>({
    queryKey: ["coach_foreign_transactions", page, limit],
    queryFn: async () => {
      const result = await repository.getForeignTransactions({page, limit});
      return {
        data: {fxTransactions: result.data.map(toLegacyForeignTransaction)},
        meta: toLegacyMeta(result.meta),
      };
    },
    enabled,
    refetchOnWindowFocus: true,
  });
};

/**
 * Legacy-compatible hook for filtering local transactions.
 *
 * @deprecated Use useFilterLocalTransactions for new code.
 */
export const useLegacyFilterTransactions = () => {
  const repository = createTransactionRepository();

  return useMutation<
    LegacyLocalTransactionsResponse,
    AxiosError<{message: string}>,
    {
      startDate?: string;
      endDate?: string;
      offering: string;
      name: string;
      page?: number;
      limit?: number;
    }
  >({
    mutationFn: async (payload) => {
      const result = await repository.filterLocalTransactions({
        startDate: payload.startDate,
        endDate: payload.endDate,
        serviceName: payload.offering,
        customerName: payload.name,
        page: payload.page,
        limit: payload.limit,
      });
      return {
        data: {localTransactions: result.data.map(toLegacyLocalTransaction)},
        meta: toLegacyMeta(result.meta),
      };
    },
    onError: (error) => {
      message.error(error.message);
      AnalyticsEvent("Coach transactions", "Filter transactions failed");
    },
    onSuccess: () => {
      AnalyticsEvent("Coach transactions", "Filter transactions success");
    },
  });
};

/**
 * Legacy-compatible hook for filtering foreign transactions.
 *
 * @deprecated Use useFilterForeignTransactions for new code.
 */
export const useLegacyFilterForeignTransactions = () => {
  const repository = createTransactionRepository();

  return useMutation<
    LegacyForeignTransactionsResponse,
    AxiosError<{message: string}>,
    {
      startDate?: string;
      endDate?: string;
      offering: string;
      name: string;
      page?: number;
      limit?: number;
    }
  >({
    mutationFn: async (payload) => {
      const result = await repository.filterForeignTransactions({
        startDate: payload.startDate,
        endDate: payload.endDate,
        serviceName: payload.offering,
        customerName: payload.name,
        page: payload.page,
        limit: payload.limit,
      });
      return {
        data: {fxTransactions: result.data.map(toLegacyForeignTransaction)},
        meta: toLegacyMeta(result.meta),
      };
    },
    onError: (error) => {
      message.error(error.message);
      AnalyticsEvent("Coach foreign transactions", "Filter foreign transactions failed");
    },
    onSuccess: () => {
      AnalyticsEvent("Coach foreign transactions", "Filter foreign transactions success");
    },
  });
};

/**
 * Legacy-compatible hook for exporting transactions CSV.
 *
 * @deprecated Use useExportTransactionsCSV for new code.
 */
export const useExportCoachTransactionsCSV = () => {
  const repository = createTransactionRepository();

  return useMutation<
    BlobPart,
    AxiosError<{message: string}>,
    {startDate: string; endDate: string; transactionType: "local" | "foreign"}
  >({
    mutationFn: (params) =>
      repository.exportTransactionsCSV({
        startDate: params.startDate,
        endDate: params.endDate,
        transactionType: params.transactionType,
      }) as Promise<BlobPart>,
    onError: (error) => {
      message.error(error.message);
      AnalyticsEvent("Coach transactions", "Coach transactions export CSV fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Coach transactions", "Coach transactions export CSV success");
    },
  });
};

// Re-export legacy hook aliases for backwards compatibility
export {useLegacyFilterTransactions as useFilterTransactions};
export {useLegacyFilterForeignTransactions as useFilterForeignTransactions};

// Alias for the domain-based filter hook (recommended for new code)
export {useDomainFilterForeignTransactions as useFilterForeignDomain};
